import { useState } from 'react'
import {
  Container,
  Paper,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
} from '@mui/material'
import { Gavel, Visibility } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useListVerdictsQuery } from '../store/api/courtroomsApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { PageHeader } from '../components/PageHeader'
import { getTablePaperSx, getFilterPaperSx } from '../theme/sectionSurfaces'

/** UAT-TC #15 (Phán quyết): Ban Thư ký xem danh sách phán quyết Thẩm phán đã nhập. */
export default function VerdictsReviewPage() {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<'all' | 'submitted' | 'published'>('all')
  const { data: cases = [] } = useGetCasesQuery()
  const { data: teams = [] } = useGetTeamsQuery()
  const { data: verdicts = [], isLoading, error } = useListVerdictsQuery(
    statusFilter === 'all' ? undefined : { status: statusFilter as 'submitted' | 'published' }
  )

  const teamName = (id?: string | null) => teams.find((t) => t.id === id)?.name || id || '—'
  const caseTitle = (id: string) => cases.find((c) => c.id === id)?.title || id

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
      <PageHeader
        title="Phán quyết — Ban Thư ký"
        subtitle="Danh sách phán quyết đã được Thẩm phán nhập, chờ công bố hoặc đã công bố."
        icon={<Gavel />}
      />

      <Paper
        elevation={0}
        sx={(t) => ({
          ...getFilterPaperSx(t),
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
        })}
      >
        <Typography variant="body2" fontWeight={500}>Lọc trạng thái:</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(_, v) => v && setStatusFilter(v)}
        >
          <ToggleButton value="all">Tất cả</ToggleButton>
          <ToggleButton value="submitted">Chờ công bố</ToggleButton>
          <ToggleButton value="published">Đã công bố</ToggleButton>
        </ToggleButtonGroup>
        <Chip label={`Tổng: ${verdicts.length}`} size="small" />
      </Paper>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">Không tải được danh sách phán quyết.</Alert>
      ) : verdicts.length === 0 ? (
        <Alert severity="info">Chưa có phán quyết nào ở trạng thái {statusFilter}.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={(t) => getTablePaperSx(t)}>
          <Table sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 220 }}><strong>Cuộc thi</strong></TableCell>
                <TableCell sx={{ minWidth: 280 }}><strong>Tóm tắt phán quyết</strong></TableCell>
                <TableCell sx={{ minWidth: 140 }}><strong>Đội thắng</strong></TableCell>
                <TableCell sx={{ minWidth: 130 }}><strong>Trạng thái</strong></TableCell>
                <TableCell sx={{ minWidth: 130 }}><strong>Ngày nhập</strong></TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}><strong>Xem</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {verdicts.map((v) => (
                <TableRow key={v.courtroom_id} hover>
                  <TableCell>{caseTitle(v.case_id)}</TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Tooltip title={v.summary || ''}>
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {v.summary || '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{teamName(v.winner_team_id)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        v.verdict_status === 'published'
                          ? 'Đã công bố'
                          : v.verdict_status === 'submitted'
                          ? 'Chờ công bố'
                          : v.verdict_status
                      }
                      color={v.verdict_status === 'published' ? 'success' : 'warning'}
                    />
                  </TableCell>
                  <TableCell>
                    {v.decided_at ? new Date(v.decided_at).toLocaleString('vi-VN') : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/courtroom?courtroomId=${v.courtroom_id}`)}
                      title="Mở phòng để xem chi tiết"
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  )
}
